"use client";

// ===================================
// PUBLIC CHAT COMPONENT - With Agentic Authentication
// components/PublicChat.tsx
// ===================================
import type React from "react";
import { useState, useEffect, useRef } from "react";
import { publicChatApi, sessionManager } from "../services/api";
import LoadingSpinner from "./LoadingSpinner";
import AgenticChecklist from "./AgenticChecklist";
import PublicAgenticResponse from "./PublicAgenticResponse";
import {
  Send,
  Bot,
  User,
  RefreshCw,
  AlertTriangle,
  Sparkles,
  Clock,
  CheckCircle,
  Shield,
} from "lucide-react";
import ReactMarkdown from "react-markdown";

interface PublicChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  metadata?: {
    type?:
      | "customer_service_rag"
      | "error_response"
      | "offline_response"
      | "offline_notice"
      | "fallback_response"
      | "agentic_notice"
      | "agentic_parameter_collection"
      | "agentic_ready"
      | "agentic_action_result"
      | "welcome";
    meta?: any;
    confidence_score?: number;
    context_chunks_used?: number;
    source_documents?: any[];
    response_metadata?: any;
    search_metadata?: any;
    intent?: {
      type: string;
      action: string;
      topic: string;
      entities?: Record<string, any>;
    };
    requires_auth?: boolean;
    eventEmitted?: boolean;
    [key: string]: any;
  };
  created_at: string;
}

interface PublicChatSession {
  id: string;
  status: string;
  created_at: string;
  rag_enabled: boolean;
  business_info: {
    name: string;
    support_available: boolean;
  };
}

interface PublicChatProps {
  isEmbedded?: boolean;
  className?: string;
  onClose?: () => void;
  onSessionCreated?: (sessionId: string) => void;
  onAgenticNotice?: (data: any) => void;
}

const PublicChat: React.FC<PublicChatProps> = ({
  isEmbedded = false,
  className = "",
  onClose,
  onSessionCreated,
  onAgenticNotice,
}) => {
  // Chat state
  const [session, setSession] = useState<PublicChatSession | null>(null);
  const [messages, setMessages] = useState<PublicChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isOfflineMode, setIsOfflineMode] = useState(false);

  const [isAgenticVerified, setIsAgenticVerified] = useState(false);

  // UI state for widget mode only
  const [isMinimized, setIsMinimized] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isTyping, setIsTyping] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);
  const initTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Always initialize when component mounts, regardless of embedded state
  useEffect(() => {
    if (!session && !isOfflineMode) {
      initializeChat();
    }
  }, []); // Empty dependency array - run once on mount

  // Emit chat ready event when session is established
  useEffect(() => {
    if (session && !isInitializing) {
      const event = new CustomEvent("chatReady");
      window.dispatchEvent(event);
    }
  }, [session, isInitializing]);

  useEffect(() => {
    const isVerified = sessionManager.isAgenticVerified();
    setIsAgenticVerified(isVerified);
  }, []);

  useEffect(() => {
    // Cleanup timeout on unmount
    return () => {
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Only scroll to bottom if user is near the bottom (within 100px)
    const messagesContainer = messagesEndRef.current?.parentElement;
    if (messagesContainer) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainer;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

      if (isNearBottom) {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [messages]);

  useEffect(() => {
    // Focus input when chat becomes ready and not minimized
    if (!isInitializing && !isMinimized && chatInputRef.current) {
      chatInputRef.current.focus();
    }
  }, [isInitializing, isMinimized]);

  useEffect(() => {
    const handleExternalMessage = (event: CustomEvent) => {
      if (event.detail?.message) {
        setNewMessage(event.detail.message);
        setShowWelcome(false);
        // Auto-send after a brief delay if we have a session
        setTimeout(() => {
          if (session || isOfflineMode) {
            sendMessage(event.detail.message);
          }
        }, 100);
      }
    };

    const handleAgenticReplay = (event: any) => {
      if (event.detail?.response) {
        const replayMessage: PublicChatMessage = {
          id: event.detail.response.id,
          role: "assistant",
          content: event.detail.response.content,
          created_at: event.detail.response.created_at,
          metadata: event.detail.response.metadata,
        };
        setMessages((prev) => [...prev, replayMessage]);
      }
    };

    const handleVerificationChange = () => {
      const isVerified = sessionManager.isAgenticVerified();
      setIsAgenticVerified(isVerified);
    };

    const handleNotification = (event: any) => {
      const { type, title, message } = event.detail;
    };

    window.addEventListener(
      "sendPublicMessage",
      handleExternalMessage as EventListener
    );
    window.addEventListener(
      "agenticActionReplayed",
      handleAgenticReplay as EventListener
    );
    window.addEventListener(
      "agenticVerificationChanged",
      handleVerificationChange
    );
    window.addEventListener("showNotification", handleNotification);

    return () => {
      window.removeEventListener(
        "sendPublicMessage",
        handleExternalMessage as EventListener
      );
      window.removeEventListener(
        "agenticActionReplayed",
        handleAgenticReplay as EventListener
      );
      window.removeEventListener(
        "agenticVerificationChanged",
        handleVerificationChange
      );
      window.removeEventListener("showNotification", handleNotification);
    };
  }, [session, isOfflineMode]);

  const initializeChat = async () => {
    // Check if API is available
    if (!publicChatApi) {
      enableOfflineMode();
      return;
    }

    try {
      setIsInitializing(true);
      setConnectionError(null);

      // Set a timeout for initialization
      initTimeoutRef.current = setTimeout(() => {
        enableOfflineMode();
      }, 10000); // 10 second timeout

      // Test connection first with a shorter timeout
      try {
        const healthResult = await Promise.race([
          publicChatApi.health(),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Health check timeout")), 5000)
          ),
        ]);
      } catch (healthError) {
        // Continue anyway - health check might not be implemented
      }

      // Gather visitor information
      const visitorInfo = {
        page_url: window.location.href,
        referrer: document.referrer,
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      const response = (await Promise.race([
        publicChatApi.createSession({
          visitor_info: visitorInfo,
        }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Session creation timeout")), 8000)
        ),
      ])) as any;

      try {
        const sessionData = response?.data?.session;

        if (!sessionData?.id) {
          throw new Error("Session ID not found in response");
        }

        const newSession: PublicChatSession = {
          id: sessionData.id,
          status: sessionData.status || "active",
          created_at: sessionData.created_at || new Date().toISOString(),
          rag_enabled: Boolean(sessionData.rag_enabled),
          business_info: sessionData.business_info || {
            name: "Pridict Ltd",
            support_available: true,
          },
        };

        setSession(newSession);

        onSessionCreated?.(newSession.id);

        const sessionEvent = new CustomEvent("publicChatSessionCreated", {
          detail: { sessionId: newSession.id },
        });
        window.dispatchEvent(sessionEvent);

        const initialResponse = response?.data?.initial_response;

        if (
          initialResponse?.user_message &&
          initialResponse?.assistant_message
        ) {
          setMessages([
            initialResponse.user_message,
            initialResponse.assistant_message,
          ]);
          setShowWelcome(false);
        } else {
          setShowWelcome(true);
          setMessages([]);
        }

        setRetryCount(0);
      } catch (parseError) {
        throw parseError;
      }
    } catch (error: any) {
      if (retryCount < 3) {
        const newRetryCount = retryCount + 1;
        setRetryCount(newRetryCount);
        setTimeout(() => {
          initializeChat();
        }, 2000 * newRetryCount);

        setConnectionError(
          `Connection attempt ${newRetryCount}/3 failed. Retrying...`
        );
        return;
      } else {
        enableOfflineMode();
        return;
      }
    } finally {
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
        initTimeoutRef.current = null;
      }
      setIsInitializing(false);
    }
  };

  const enableOfflineMode = () => {
    setIsOfflineMode(true);
    setIsInitializing(false);
    setConnectionError(null);

    const mockSession: PublicChatSession = {
      id: "offline-session",
      status: "offline",
      created_at: new Date().toISOString(),
      rag_enabled: false,
      business_info: {
        name: "Pridict Ltd",
        support_available: false,
      },
    };

    setSession(mockSession);

    const offlineMessage: PublicChatMessage = {
      id: "offline-notice",
      role: "assistant",
      content:
        "I'm currently in offline mode. While I can't connect to our live support system right now, you can still type your questions and I'll do my best to help with general information. For urgent matters, please contact us directly at support@pridict.com.",
      created_at: new Date().toISOString(),
      metadata: { type: "offline_notice" },
    };

    setMessages([offlineMessage]);
    setShowWelcome(false);
  };

  const sendMessage = async (messageContent?: string) => {
    const userMessageContent = messageContent || newMessage.trim();
    if (!userMessageContent || isLoading || (!session && !isOfflineMode))
      return;

    setNewMessage("");
    setIsLoading(true);
    setIsTyping(true);
    setShowWelcome(false);

    const userMessage: PublicChatMessage = {
      id: Date.now().toString(),
      role: "user",
      content: userMessageContent,
      created_at: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);

    try {
      let assistantMessage: PublicChatMessage;

      if (isOfflineMode || !session || session.status === "offline") {
        await new Promise((resolve) => setTimeout(resolve, 1000));

        assistantMessage = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: getOfflineResponse(userMessageContent),
          created_at: new Date().toISOString(),
          metadata: { type: "offline_response" },
        };

        setMessages((prev) => [...prev, assistantMessage]);
      } else {
        // Online mode - send to API
        const response = await publicChatApi.sendMessage(session.id, {
          message: userMessageContent,
          visitor_info: {
            timestamp: new Date().toISOString(),
            message_count: messages.length + 1,
          },
        });

        const responseData = response.data;
        const isAuthRequired =
          responseData?.assistant_message?.metadata?.requires_auth === true ||
          responseData?.requires_auth === true ||
          responseData?.error === "AGENTIC_AUTH_REQUIRED" ||
          responseData?.assistant_message?.metadata?.type === "agentic_notice";

        if (isAuthRequired) {
          const authNotice: PublicChatMessage = {
            id: Date.now().toString() + "-auth",
            role: "system",
            content:
              "This action requires authentication. Please verify your access to continue.",
            created_at: new Date().toISOString(),
            metadata: {
              type: "agentic_notice",
              requires_auth: true,
            },
          };
          setMessages((prev) => [...prev, authNotice]);

          onAgenticNotice?.({
            intent: responseData?.intent,
            messageId: userMessage.id,
            sessionId: session.id,
          });

          const noticeEvent = new CustomEvent("agenticNotice", {
            detail: {
              intent: responseData?.intent || {
                type: "AGENTIC_ACTION",
                action: "UNKNOWN",
                topic: "SUPPORT",
              },
              messageId: userMessage.id,
              sessionId: session.id,
              originalMessage: userMessageContent,
            },
          });
          window.dispatchEvent(noticeEvent);
        } else {
          // Normal successful response
          assistantMessage = responseData.assistant_message;
          setMessages((prev) => [...prev, assistantMessage]);
        }
      }

      // Emit new message event for widget
      if (assistantMessage!) {
        const newMessageEvent = new CustomEvent("newChatMessage", {
          detail: { message: assistantMessage },
        });
        window.dispatchEvent(newMessageEvent);
      }

      if (isMinimized) {
        setUnreadCount((prev) => prev + 1);
      }

      setConnectionError(null);
    } catch (error: any) {
      const isAuthRequired =
        (error.response?.status === 401 &&
          error.response?.data?.error?.code === "AGENT_AUTH_REQUIRED") ||
        error.response?.data?.error === "AGENTIC_AUTH_REQUIRED";

      if (isAuthRequired) {
        const authNotice: PublicChatMessage = {
          id: Date.now().toString() + "-auth",
          role: "system",
          content:
            "This action requires authentication. Please verify your access to continue.",
          created_at: new Date().toISOString(),
          metadata: {
            type: "agentic_notice",
            requires_auth: true,
          },
        };
        setMessages((prev) => [...prev, authNotice]);

        const noticeEvent = new CustomEvent("agenticNotice", {
          detail: {
            intent: error.response?.data?.intent || {
              type: "AGENTIC_ACTION",
              action: "UNKNOWN",
              topic: "SUPPORT",
            },
            messageId: userMessage.id,
            sessionId: session?.id,
            originalMessage: userMessageContent,
          },
        });
        window.dispatchEvent(noticeEvent);
      } else {
        // Fall back to offline mode if online request fails
        if (!isOfflineMode) {
          const offlineResponse: PublicChatMessage = {
            id: (Date.now() + 1).toString(),
            role: "assistant",
            content: `I'm having trouble connecting to our support system right now. Here's what I can tell you: ${getOfflineResponse(
              userMessageContent
            )}\n\nFor real-time assistance, please contact our support team directly at support@pridict.com.`,
            created_at: new Date().toISOString(),
            metadata: { type: "fallback_response" },
          };

          setMessages((prev) => [...prev, offlineResponse]);
        }
      }
    } finally {
      setIsLoading(false);
      setIsTyping(false);
    }
  };

  const getOfflineResponse = (userMessage: string): string => {
    const lowerMessage = userMessage.toLowerCase();

    if (
      lowerMessage.includes("service") ||
      lowerMessage.includes("what do you offer")
    ) {
      return "Pridict Ltd offers AI-powered solutions and customer support services. We specialize in intelligent automation and customer service technology. For detailed information about our services, please visit our website or contact us directly at support@pridict.com.";
    }

    if (
      lowerMessage.includes("start") ||
      lowerMessage.includes("get started")
    ) {
      return "To get started with our services, you can contact our team at support@pridict.com or visit our website. Our team will guide you through our offerings and help you find the right solution for your needs.";
    }

    if (
      lowerMessage.includes("price") ||
      lowerMessage.includes("cost") ||
      lowerMessage.includes("pricing")
    ) {
      return "For pricing information, please contact our sales team at support@pridict.com. They'll provide you with detailed pricing based on your specific requirements.";
    }

    if (
      lowerMessage.includes("support") ||
      lowerMessage.includes("help") ||
      lowerMessage.includes("technical")
    ) {
      return "For technical support and assistance, please reach out to our support team at support@pridict.com. They're available to help with any technical questions or issues you may have.";
    }

    if (
      lowerMessage.includes("ai") ||
      lowerMessage.includes("artificial intelligence")
    ) {
      return "We provide AI-powered solutions to help businesses automate and improve their customer service. Our AI technology can handle customer inquiries, provide instant responses, and integrate with existing systems. Contact us at support@pridict.com to learn more.";
    }

    return "Thank you for your question. While I'm currently in offline mode and can't access our full knowledge base, I'd be happy to help connect you with our team. Please contact us at support@pridict.com for detailed assistance with your inquiry.";
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendMessage();
  };

  const retryConnection = async () => {
    setIsOfflineMode(false);
    setSession(null);
    setMessages([]);
    setShowWelcome(true);
    setRetryCount(0);
    await initializeChat();
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
    if (isMinimized) {
      setUnreadCount(0);
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const renderMessage = (message: PublicChatMessage) => {
    const isUser = message.role === "user";
    const isSystem = message.role === "system";
    const isRAGResponse = message.metadata?.type === "customer_service_rag";
    const isError = message.metadata?.type === "error_response";
    const isOffline =
      message.metadata?.type === "offline_response" ||
      message.metadata?.type === "offline_notice" ||
      message.metadata?.type === "fallback_response";
    const isAgenticNotice = message.metadata?.type === "agentic_notice";
    const isAgenticChecklist =
      message.metadata?.type === "agentic_parameter_collection";
    const isAgenticReady = message.metadata?.type === "agentic_ready";
    const isAgenticResult = message.metadata?.type === "agentic_action_result";
    const isWelcome = message.metadata?.type === "welcome";

    return (
      <div
        key={message.id}
        className={`flex items-start space-x-3 mb-6 ${
          isUser ? "flex-row-reverse space-x-reverse" : ""
        }`}
      >
        {/* Avatar */}
        <div className="flex-shrink-0">
          {isUser ? (
            <div className="w-9 h-9 rounded-full bg-[#020617] border border-[#334155] flex items-center justify-center shadow-sm">
              <User className="w-5 h-5 text-white" />
            </div>
          ) : isSystem || isAgenticNotice ? (
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shadow-sm">
              <Shield className="w-5 h-5 text-white" />
            </div>
          ) : isAgenticChecklist ? (
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-yellow-300 to-yellow-500 flex items-center justify-center shadow-sm text-white">
              📝
            </div>
          ) : isAgenticReady ? (
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center shadow-sm text-white">
              ✅
            </div>
          ) : isAgenticResult ? (
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center shadow-sm text-white">
              ⚡
            </div>
          ) : (
            <div
              className={`w-9 h-9 rounded-full ${
                isError
                  ? "bg-gradient-to-br from-red-500 to-red-600"
                  : isOffline
                  ? "bg-gradient-to-br from-orange-500 to-orange-600"
                  : "bg-[#8b3dff]"
              } flex items-center justify-center shadow-sm`}
            >
              {isError ? (
                <AlertTriangle className="w-5 h-5 text-white" />
              ) : (
                <Bot className="w-5 h-5 text-white" />
              )}
            </div>
          )}
        </div>

        {/* Message Content */}
        <div className={`max-w-md ${isUser ? "text-right" : "text-left"}`}>
          {isAgenticChecklist ? (
            <AgenticChecklist content={message.content} />
          ) : isAgenticResult ? (
            <PublicAgenticResponse message={message} />
          ) : (
            <div
              className={`px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                isUser
                  ? "bg-[#8b3dff] text-white rounded-br-lg shadow-sm"
                  : isAgenticNotice || isSystem
                  ? "bg-yellow-900/20 border border-yellow-600/30 text-yellow-200 rounded-bl-lg"
                  : isError
                  ? "bg-red-50 border border-red-200 text-red-800 rounded-bl-lg"
                  : isOffline
                  ? "bg-orange-50 border border-orange-200 text-orange-800 rounded-bl-lg"
                  : isRAGResponse
                  ? "bg-[#020617] border border-[#334155] text-white rounded-bl-lg"
                  : "bg-[#020617] border border-[#334155] text-white rounded-bl-lg"
              }`}
            >
              <div className="whitespace-pre-wrap leading-relaxed">
                <ReactMarkdown
                  components={{
                    strong: ({ node, ...props }) => (
                      <strong className="font-bold text-gray-100" {...props} />
                    ),
                    code: ({ node, ...props }) => (
                      <code
                        className="px-1 py-0.5 bg-gray-300 rounded text-sm"
                        {...props}
                      />
                    ),
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              </div>

              {/* Status indicators */}
              {isOffline && (
                <div className="mt-2 flex items-center justify-end space-x-1">
                  <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                  <span className="text-xs text-orange-600 font-medium">
                    Offline Mode
                  </span>
                </div>
              )}

              {isRAGResponse &&
                message.metadata?.confidence_score !== undefined && (
                  <div className="mt-2 flex items-center justify-end space-x-1">
                    <Sparkles className="w-3 h-3 text-purple-500" />
                    <span className="text-xs text-[#8b3dff] font-medium">
                      AI-assisted
                    </span>
                  </div>
                )}
            </div>
          )}

          {/* Timestamp */}
          <div
            className={`text-xs mt-1 flex items-center ${
              isUser ? "text-gray-500 justify-end" : "text-gray-400"
            }`}
          >
            <Clock className="w-3 h-3 mr-1" />
            {new Date(message.created_at).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderWelcomeMessage = () => (
    <div className="text-center py-8 px-6 ">
      <div className="w-16 h-16 bg-[#8b3dff] rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4 shadow-lg">
        👋
      </div>
      <h3 className="text-xl font-semibold text-white mb-3 flex items-center justify-center space-x-2">
        <span>Welcome to {session?.business_info?.name || "Our Support"}!</span>
        {isAgenticVerified && (
          <CheckCircle className="w-5 h-5 text-green-400" />
        )}
      </h3>
      <p className="text-[#64748b] mb-6 max-w-md mx-auto">
        {isOfflineMode
          ? "I'm currently in offline mode, but I can still help with general questions about our services."
          : isAgenticVerified
          ? "Enhanced support features enabled. I'm here to help you with any questions about our services."
          : "I'm here to help you with any questions about our services. What can I assist you with today?"}
      </p>
      <div className="space-y-3 max-w-sm mx-auto">
        <button
          onClick={() => sendMessage("What services do you offer?")}
          disabled={isInitializing}
          className="block w-full text-left px-4 py-3 text-sm text-[#64748b] hover:bg-[#0f172a] rounded-xl border border-[#334155] transition-all duration-200 hover:shadow-sm disabled:opacity-50"
        >
          💼 What services do you offer?
        </button>
        <button
          onClick={() => sendMessage("How can I get started?")}
          disabled={isInitializing}
          className="block w-full text-left px-4 py-3 text-sm text-[#64748b] hover:bg-[#0f172a] rounded-xl border border-[#334155] transition-all duration-200 hover:shadow-sm disabled:opacity-50"
        >
          🚀 How can I get started?
        </button>
        <button
          onClick={() => sendMessage("I need help with my account")}
          disabled={isInitializing}
          className="block w-full text-left px-4 py-3 text-sm text-[#64748b] hover:bg-[#0f172a] rounded-xl border border-[#334155] transition-all duration-200 hover:shadow-sm disabled:opacity-50"
        >
          🔧 I need help with my account
        </button>
      </div>
    </div>
  );

  const renderErrorState = () => (
    <div className="text-center py-8 px-6">
      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <AlertTriangle className="w-8 h-8 text-red-600" />
      </div>
      <h3 className="text-xl font-semibold text-white mb-3">
        Connection Issue
      </h3>
      <p className="text-[#64748b] mb-6 max-w-md mx-auto">{connectionError}</p>
      <button
        onClick={retryConnection}
        disabled={isInitializing}
        className="inline-flex items-center space-x-2 px-6 py-3 bg-[#8b3dff] text-white rounded-xl hover:bg-purple-700 transition-colors shadow-sm disabled:opacity-50"
      >
        <RefreshCw className="w-4 h-4" />
        <span>Retry Connection</span>
      </button>
      <div className="mt-6 pt-6 border-t border-[#334155]">
        <p className="text-sm text-gray-500">
          You can also reach us directly at{" "}
          <a
            href="mailto:support@pridict.com"
            className="text-[#8b3dff] hover:text-purple-800 font-medium"
          >
            support@pridict.com
          </a>
        </p>
      </div>
    </div>
  );

  return (
    <div className={`w-full h-[480px] bg-[#1e293b] flex flex-col ${className}`}>
      {/* Messages Container */}
      <div
        className="flex-1 overflow-y-auto px-6 py-4"
        style={{ scrollBehavior: "smooth" }}
      >
        {isInitializing ? (
          <div className="flex items-center justify-center h-full bg-[#020617]">
            <div className="text-center">
              <LoadingSpinner size="lg" message="" />
              <p className="text-[#64748b] mt-4">
                {retryCount > 0
                  ? `Connecting to support... (attempt ${retryCount + 1}/3)`
                  : "Connecting to support..."}
              </p>
            </div>
          </div>
        ) : connectionError && !session && !isOfflineMode ? (
          renderErrorState()
        ) : showWelcome && messages.length === 0 ? (
          renderWelcomeMessage()
        ) : (
          <>
            {messages.map(renderMessage)}

            {/* Typing Indicator */}
            {isLoading && (
              <div className="flex items-start space-x-3 mb-6">
                <div className="w-9 h-9 rounded-full bg-[#8b3dff] flex items-center justify-center shadow-sm">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div className="max-w-md">
                  <div className="bg-[#020617] border border-[#334155] px-4 py-3 rounded-2xl rounded-bl-md shadow-sm">
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div
                        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: "0.1s" }}
                      ></div>
                      <div
                        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: "0.2s" }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Message Input */}
      <div className="bg-[#1e293b] border-t border-[#334155] px-6 py-4">
        <form
          onSubmit={handleSendMessage}
          className="flex items-end justify-center w-full max-w-3xl mx-auto space-x-3"
        >
          <div className="flex-1 relative flex justify-center">
            <textarea
              ref={chatInputRef}
              value={newMessage}
              onChange={(e) => {
                setNewMessage(e.target.value);
                const target = e.target;
                target.style.height = "auto"; // reset
                target.style.height = `${target.scrollHeight}px`; // grow with content
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage(e);
                }
              }}
              placeholder={
                isInitializing
                  ? "Connecting..."
                  : isOfflineMode
                  ? "Type your message... (offline mode)"
                  : "Type your message here... (Shift+Enter for new line)"
              }
              className="w-full text-white bg-[#020617] border border-[#334155] rounded-2xl px-5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none overflow-hidden transition-all duration-200 leading-relaxed shadow-sm"
              disabled={isLoading || isInitializing}
              rows={1}
              style={{ minHeight: "50px", maxHeight: "200px" }}
            />
          </div>

          {/* Send Button */}
          <button
            type="submit"
            disabled={!newMessage.trim() || isLoading || isInitializing}
            className="bg-[#8b3dff] text-white px-5 h-[50px] rounded-2xl hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md flex items-center justify-center"
          >
            <Send className="w-5 h-5" />
          </button>
        </form>

        <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center space-x-2">
            <Sparkles className="w-3 h-3" />
            <span>
              {isOfflineMode
                ? "Offline mode • Limited responses"
                : "AI-powered support • Instant responses"}
            </span>
            {isAgenticVerified && !isOfflineMode && (
              <span className="text-green-400 ml-2 flex items-center space-x-1">
                <CheckCircle className="w-3 h-3" />
                <span>Enhanced Access Active</span>
              </span>
            )}
            {connectionError && !isOfflineMode && (
              <span className="text-yellow-600 ml-2">
                ⚠️ Limited connectivity
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {isOfflineMode && (
              <button
                onClick={retryConnection}
                className="text-[#8b3dff] hover:text-purple-800 font-medium"
                disabled={isInitializing}
              >
                Retry Connection
              </button>
            )}
            {session?.rag_enabled && !isOfflineMode && (
              <div className="flex items-center space-x-1 text-[#8b3dff]">
                <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                <span>Knowledge base enabled</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicChat;
